/**
 * `synkro local-cc <subcommand>` — manage the local pueue-backed Claude Code
 * session that powers grading and intent classification when the inference
 * provider toggle is set to local-cc.
 */

import { spawnSync } from 'node:child_process';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { installLocalCC, SESSION_DIR, PLUGIN_PATH, RUN_SCRIPT_PATH, PLUGIN_SETTINGS_PATH, CLAUDE_JSON_PATH, TMUX_SESSION_NAME, CHANNEL_2_PORT, TMUX_SESSION_NAME_2 } from '../local-cc/install.js';
import { readRecentTurns, followTurns, TURN_LOG_PATH, type TurnEntry } from '../local-cc/turnLog.js';
import {
  assertClaudeInstalled,
  assertPueueInstalled,
  assertTmuxInstalled,
  ensureRunning,
  findTask,
  startTask,
  stopTask,
  tailLogs,
  waitForChannelReady,
  CHANNEL_PRIMARY,
  CHANNEL_SECONDARY,
} from '../local-cc/pueue.js';
import { isLocalCCEnabled } from '../local-cc/settings.js';
import { refreshCreds, needsKeychainBridge, credsAreStale, CLAUDE_CREDS_FILE } from '../local-cc/macKeychain.js';
import { dockerStatus, dockerStop, dockerUpdate, waitForContainerReady, waitForWorkersReady, resolveWorkerConfig } from '../local-cc/dockerInstall.js';
import { reconcileHarness, syncSkillFiles } from './install.js';
import { readFileSync as fsReadFileSync, existsSync as fsExistsSync } from 'node:fs';

const SYNKRO_CONFIG_PATH = join(homedir(), '.synkro', 'config.env');

/**
 * Resolution order matches commands/install.ts: live env var first, then the
 * persisted config.env value, then 'bare-host'. Kept inline rather than
 * imported from install.ts so this module stays loadable when the install
 * command tree is pruned in future cleanups.
 */
function deploymentMode(): 'bare-host' | 'docker' {
  const env = (process.env.SYNKRO_DEPLOYMENT_MODE || '').toLowerCase();
  if (env === 'docker') return 'docker';
  if (env === 'bare-host') return 'bare-host';
  try {
    if (fsExistsSync(SYNKRO_CONFIG_PATH)) {
      const m = fsReadFileSync(SYNKRO_CONFIG_PATH, 'utf-8').match(/^SYNKRO_DEPLOYMENT_MODE='([^']*)'/m);
      if (m && m[1] === 'docker') return 'docker';
    }
  } catch { /* fall through */ }
  return 'bare-host';
}

function inDockerMode(): boolean { return deploymentMode() === 'docker'; }
import { submitToChannel, isChannelAvailable, CHANNEL_HOST, CHANNEL_PORT } from '../local-cc/client.js';
import { getAccessToken, ensureValidToken } from '../auth/stub.js';
import { existsSync, readFileSync, writeFileSync } from 'node:fs';

function printHelp(): void {
  console.log(`synkro local-cc — manage the local Claude Code inference session

OVERVIEW
  Routes Synkro's grading and intent-classification work through a long-running
  Claude Code session on this machine instead of remote Inngest+Gemini.

  When enabled, three call sites switch over:
    • security grading on edit/bash hooks
    • intent classification (agent input → structured intent)
    • remediate intent classification

  The session is hosted in a detached tmux session managed by a pueue task.
  The CLI talks to it via Claude Code's channels API: a Bun MCP plugin that
  pushes events into the session and receives Claude's responses through a
  \`reply\` MCP tool.

USAGE
  synkro local-cc <subcommand> [args]

SUBCOMMANDS
  enable                Install plugin, start pueue task, flip toggle to local-cc
  disable               Flip toggle back to inngest (pueue task left running)
  status                Show provider toggle, pueue task state, channel reachability
  start                 Idempotently bring up the pueue task + wait for the channel
  stop                  Kill the tmux session and remove the pueue task
  restart               stop, then start
  install               Regenerate ~/.synkro/cc_sessions/ files (plugin, runner, settings)
  logs [N] [--raw] [--live]
                        Show the last N (default 20) channel turns: when, role,
                        duration, severity, request preview.
                        --raw / -r   include full request/response payloads
                        --live / -f  tail the log; print new turns as they arrive
                                     (Ctrl-C to exit)
                        --tmux       escape hatch — print the raw pueue/tmux
                                     pane log instead
  attach [--readonly]   Attach to the tmux session hosting claude (Ctrl-B D to detach;
                        --readonly / -r to attach view-only)
  test                  Send a smoke-test classification through the channel
  help                  Show this message

CONFIGURATION
  Provider toggle:
    Stored server-side in your inference settings (gradingProvider).
    Toggle via:  synkro local-cc enable / disable
    Or via dashboard inference settings (set grading provider to claude-code).

  Claude Code session settings (scoped to ~/.synkro/cc_sessions only):
    ${PLUGIN_SETTINGS_PATH}
    Currently sets {"fastMode": true}. Edit to add other CC settings for this
    session without affecting your other CC projects.

  MCP server registration (for the channel plugin):
    ${CLAUDE_JSON_PATH}
    A 'synkro-local' entry under mcpServers, pointing at:
      bun ${PLUGIN_PATH}
    Workspace trust is also pre-accepted under projects[\`${SESSION_DIR}\`].

  Channel runtime files:
    ${RUN_SCRIPT_PATH}
      bash wrapper that pueue invokes; owns the tmux session lifecycle.
    ${PLUGIN_PATH}
      Bun MCP channel plugin (auto-generated, do not edit).
    127.0.0.1:${CHANNEL_PORT}
      Loopback TCP endpoint the CLI POSTs to in order to submit a request.

ENVIRONMENT VARIABLES
  SYNKRO_CHANNEL_PORT       Override the TCP port used by both the plugin and
                            the CLI client (loopback only). Default: 8929
  SYNKRO_CHANNEL_TIMEOUT_MS Per-request timeout inside the channel plugin
                            (default: 120000)

REQUIRED TOOLS
  claude    Claude Code CLI, authenticated to your subscription
  pueue     pueue + pueued (https://github.com/Nukesor/pueue) running
  tmux      For detached pty around claude
  bun       Runtime for the MCP channel plugin

TROUBLESHOOTING
  • Channel unreachable after \`enable\`?
      synkro local-cc logs           # check pueue task output
      tmux attach -t ${TMUX_SESSION_NAME}    # see what claude is showing
  • Stale state after a crash:
      synkro local-cc stop && synkro local-cc start
  • Want to inspect or interact with the live session:
      synkro local-cc attach
`);
}

const CONFIG_PATH = join(homedir(), '.synkro', 'config.env');

function readGatewayUrl(): string {
  if (existsSync(CONFIG_PATH)) {
    const m = readFileSync(CONFIG_PATH, 'utf-8').match(/^SYNKRO_GATEWAY_URL='([^']*)'/m);
    if (m) return m[1];
  }
  return 'https://api.synkro.sh';
}

function updateLocalInferenceFlag(enabled: boolean): void {
  if (!existsSync(CONFIG_PATH)) return;
  let content = readFileSync(CONFIG_PATH, 'utf-8');
  const flag = enabled ? 'yes' : 'no';
  if (content.includes('SYNKRO_LOCAL_INFERENCE=')) {
    content = content.replace(/^SYNKRO_LOCAL_INFERENCE='[^']*'/m, `SYNKRO_LOCAL_INFERENCE='${flag}'`);
  } else {
    content = content.trimEnd() + `\nSYNKRO_LOCAL_INFERENCE='${flag}'\n`;
  }
  writeFileSync(CONFIG_PATH, content, 'utf-8');
}

async function setServerGradingProvider(provider: 'claude-code' | null): Promise<void> {
  await ensureValidToken();
  const jwt = getAccessToken();
  if (!jwt) throw new Error('Not authenticated. Run `synkro install` first.');
  const gatewayUrl = readGatewayUrl();
  const body = provider
    ? { roles: { grading: { provider, model: 'default' } } }
    : { roles: { grading: { provider: null, model: null } } };
  const resp = await fetch(`${gatewayUrl}/api/settings/inference?scope=user`, {
    method: 'PUT',
    headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to update inference settings: ${resp.status} ${text.slice(0, 200)}`);
  }
}

async function cmdStatus(): Promise<void> {
  console.log(`Local inference: ${isLocalCCEnabled() ? 'enabled' : 'disabled'}`);
  console.log(`Deployment mode: ${deploymentMode()}`);

  if (inDockerMode()) {
    const status = dockerStatus();
    if (!status.running) {
      console.log('synkro-server container: not running');
      console.log('Run `synkro install` (with SYNKRO_DEPLOYMENT_MODE=docker) to provision.');
    } else {
      console.log(`synkro-server container: running (${status.image})`);
      try {
        const r = await fetch(`${status.healthz}healthz`, { signal: AbortSignal.timeout(3_000) });
        console.log(`Health probe: ${r.ok ? 'ok' : `HTTP ${r.status}`}`);
      } catch (err) {
        console.log(`Health probe: ${(err as Error).message}`);
      }
    }
    if (needsKeychainBridge()) {
      console.log(`Keychain creds: ${credsAreStale() ? 'STALE — run `synkro local-cc refresh-creds`' : 'fresh'}`);
    }
    return;
  }

  try {
    assertPueueInstalled();
  } catch (err) {
    console.log(`Pueue: NOT AVAILABLE (${(err as Error).message})`);
    return;
  }

  // Channel 1 (judge: bash + edit, port 8929)
  const t = findTask(CHANNEL_PRIMARY);
  if (!t) {
    console.log('Channel 1 (judge) pueue task: not present');
  } else {
    console.log(`Channel 1 (judge) pueue task: id=${t.id} status=${t.status}`);
  }
  const ch1Up = await isChannelAvailable();
  console.log(`Channel 1 ${CHANNEL_HOST}:${CHANNEL_PORT}: ${ch1Up ? 'reachable' : 'unreachable'}`);
  const tmux1 = spawnSync('tmux', ['has-session', '-t', `=${TMUX_SESSION_NAME}`], { encoding: 'utf-8' });
  console.log(`tmux '${TMUX_SESSION_NAME}': ${tmux1.status === 0 ? 'live' : 'absent'}`);

  // Channel 2 (CWE, port 8930)
  const t2 = findTask(CHANNEL_SECONDARY);
  if (!t2) {
    console.log('Channel 2 (CWE) pueue task: not present');
  } else {
    console.log(`Channel 2 (CWE) pueue task: id=${t2.id} status=${t2.status}`);
  }
  const ch2Up = await isChannelAvailable(CHANNEL_2_PORT);
  console.log(`Channel 2 ${CHANNEL_HOST}:${CHANNEL_2_PORT}: ${ch2Up ? 'reachable' : 'unreachable'}`);
  const tmux2 = spawnSync('tmux', ['has-session', '-t', `=${TMUX_SESSION_NAME_2}`], { encoding: 'utf-8' });
  console.log(`tmux '${TMUX_SESSION_NAME_2}': ${tmux2.status === 0 ? 'live' : 'absent'}`);
}

async function cmdEnable(): Promise<void> {
  assertClaudeInstalled();
  assertPueueInstalled();
  assertTmuxInstalled();
  console.log('Installing local-CC channel plugin...');
  const r = installLocalCC();
  console.log(`  plugin: ${r.pluginPath}`);
  console.log(`  cwd:    ${r.sessionDir}`);
  console.log('Starting channel 1 (judge)...');
  const t1 = ensureRunning({ channel: CHANNEL_PRIMARY });
  console.log(`  task: id=${t1.id} status=${t1.status}`);
  console.log('Starting channel 2 (CWE)...');
  const t2 = ensureRunning({ channel: CHANNEL_SECONDARY });
  console.log(`  task: id=${t2.id} status=${t2.status}`);
  console.log('Waiting for channels (auto-confirming any CC prompts)...');
  const [ready1, ready2] = await Promise.all([
    waitForChannelReady(CHANNEL_PORT, 60_000, CHANNEL_HOST, CHANNEL_PRIMARY.tmuxSession),
    waitForChannelReady(CHANNEL_2_PORT, 60_000, CHANNEL_HOST, CHANNEL_SECONDARY.tmuxSession),
  ]);
  if (ready1) console.log(`  channel 1 ready at ${CHANNEL_HOST}:${CHANNEL_PORT}`);
  else console.warn(`  ⚠ channel 1 did not come up within 60s — check \`synkro local-cc logs\``);
  if (ready2) console.log(`  channel 2 ready at ${CHANNEL_HOST}:${CHANNEL_2_PORT}`);
  else console.warn(`  ⚠ channel 2 (CWE) did not come up within 60s`);
  console.log('Updating inference settings...');
  await setServerGradingProvider('claude-code');
  updateLocalInferenceFlag(true);
  console.log('Grading provider set to claude-code (local inference enabled).');
}

async function cmdDisable(): Promise<void> {
  console.log('Updating inference settings...');
  await setServerGradingProvider(null);
  updateLocalInferenceFlag(false);
  console.log('Grading provider cleared (remote inference restored). Pueue task left running — use `synkro local-cc stop` to terminate.');
}

async function warmChannels(ready1: boolean, ready2: boolean): Promise<void> {
  const warmups: Promise<void>[] = [];
  if (ready1) {
    warmups.push(
      submitToChannel('grade-bash', 'Proposed command: echo hello\nUser intent: warmup\nRecent user messages: []\nRecent actions: []\nOrg rules: []\n', { timeoutMs: 30_000 })
        .then(() => console.log('  channel 1 warm.'))
        .catch(() => console.log('  channel 1 warmup skipped (non-fatal).'))
    );
  }
  if (ready2) {
    warmups.push(
      submitToChannel('grade-cwe', 'File: /tmp/warmup.ts\nContent (first 4000 chars):\nconsole.log("hello");\n\nCWE rules to check against:\n[]\n', { timeoutMs: 30_000, port: CHANNEL_2_PORT })
        .then(() => console.log('  channel 2 warm.'))
        .catch(() => console.log('  channel 2 warmup skipped (non-fatal).'))
    );
  }
  if (warmups.length) {
    console.log('Warming up inference...');
    await Promise.all(warmups);
  }
}

async function cmdStart(rest: string[] = []): Promise<void> {
  if (inDockerMode()) {
    // With worker flags, `start` (re)creates the container with the requested
    // pool config. Without flags it just ensures the container is running.
    if (rest.length > 0) {
      const { claudeWorkers, cursorWorkers } = resolveWorkerConfig(rest);
      console.log(`Starting synkro-server container (${claudeWorkers} claude + ${cursorWorkers} cursor)...`);
      await dockerUpdate({ claudeWorkers, cursorWorkers });
      const ready = await waitForContainerReady(60_000);
      console.log(ready ? '✓ container ready' : '⚠ container did not pass /healthz within 60s');
      return;
    }
    // Container is normally managed by docker's `--restart unless-stopped`,
    // so "start" really just means: ensure it's running and healthy. If it's
    // not even installed, point the user at `synkro install`.
    const status = dockerStatus();
    if (!status.running) {
      console.warn('synkro-server container is not running. Run `synkro install` to provision it.');
      process.exitCode = 1;
      return;
    }
    console.log('synkro-server container already running — waiting for /healthz...');
    const ready = await waitForContainerReady(60_000);
    console.log(ready ? `✓ container ready (${status.healthz})` : '⚠ container did not pass /healthz within 60s');
    return;
  }

  assertClaudeInstalled();
  assertPueueInstalled();
  assertTmuxInstalled();
  const t1 = ensureRunning({ channel: CHANNEL_PRIMARY });
  console.log(`Channel 1 (judge): id=${t1.id} status=${t1.status}`);
  const t2 = ensureRunning({ channel: CHANNEL_SECONDARY });
  console.log(`Channel 2 (CWE):   id=${t2.id} status=${t2.status}`);
  const [ready1, ready2] = await Promise.all([
    waitForChannelReady(CHANNEL_PORT, 60_000, CHANNEL_HOST, CHANNEL_PRIMARY.tmuxSession),
    waitForChannelReady(CHANNEL_2_PORT, 60_000, CHANNEL_HOST, CHANNEL_SECONDARY.tmuxSession),
  ]);
  console.log(ready1 ? `channel 1 ready (${CHANNEL_PORT}).` : '⚠ channel 1 did not come up within 60s.');
  console.log(ready2 ? `channel 2 ready (${CHANNEL_2_PORT}).` : '⚠ channel 2 (CWE) did not come up within 60s.');
  await warmChannels(ready1, ready2);
}

function cmdStop(): void {
  if (inDockerMode()) {
    dockerStop();
    console.log('synkro-server container stopped and removed.');
    return;
  }
  stopTask(CHANNEL_PRIMARY);
  stopTask(CHANNEL_SECONDARY);
  console.log('Both channels stopped.');
}

async function cmdRestart(rest: string[] = []): Promise<void> {
  if (inDockerMode()) {
    const { explicit } = resolveWorkerConfig(rest);
    let claudeWorkers: number;
    let cursorWorkers: number;
    if (explicit) {
      ({ claudeWorkers, cursorWorkers } = resolveWorkerConfig(rest));
    } else {
      const reconciled = reconcileHarness();
      if (reconciled) {
        ({ claudeWorkers, cursorWorkers } = reconciled);
      } else {
        ({ claudeWorkers, cursorWorkers } = resolveWorkerConfig(rest));
      }
    }
    console.log(`Restarting synkro-server container (${claudeWorkers} claude + ${cursorWorkers} cursor, pulling latest image)...`);
    await dockerUpdate({ claudeWorkers, cursorWorkers });
    const ready = await waitForContainerReady(60_000);
    console.log(ready ? '✓ container ready' : '⚠ container did not pass /healthz within 60s');
    if (ready) {
      const workersUp = await waitForWorkersReady(30_000);
      console.log(workersUp ? '✓ workers ready' : '⚠ workers did not register within 30s');
      if (workersUp) await syncSkillFiles();
    }
    return;
  }
  stopTask(CHANNEL_PRIMARY);
  stopTask(CHANNEL_SECONDARY);
  const t1 = startTask({ channel: CHANNEL_PRIMARY });
  const t2 = startTask({ channel: CHANNEL_SECONDARY });
  console.log(`Channel 1 restarted: id=${t1.id} status=${t1.status}`);
  console.log(`Channel 2 restarted: id=${t2.id} status=${t2.status}`);
  const [ready1, ready2] = await Promise.all([
    waitForChannelReady(CHANNEL_PORT, 60_000, CHANNEL_HOST, CHANNEL_PRIMARY.tmuxSession),
    waitForChannelReady(CHANNEL_2_PORT, 60_000, CHANNEL_HOST, CHANNEL_SECONDARY.tmuxSession),
  ]);
  console.log(ready1 ? `channel 1 ready (${CHANNEL_PORT}).` : '⚠ channel 1 did not come up within 60s.');
  console.log(ready2 ? `channel 2 ready (${CHANNEL_2_PORT}).` : '⚠ channel 2 (CWE) did not come up within 60s.');
  await warmChannels(ready1, ready2);
}

function relativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return iso;
  const sec = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function colorize(s: string, code: number): string {
  if (!process.stdout.isTTY) return s;
  return `[${code}m${s}[0m`;
}

function statusGlyph(t: TurnEntry): string {
  if (t.status === 'ok') return colorize('✓', 32);     // green check
  if (t.status === 'timeout') return colorize('⏱', 33); // yellow clock
  return colorize('✗', 31);                            // red X
}

function severityCell(t: TurnEntry): string {
  if (t.severity) {
    const sev = t.severity;
    if (sev === 'block' || sev === 'violations' || sev === 'unclear') return colorize(sev, 33);
    return colorize(sev, 36); // cyan for everything else
  }
  if (t.error) return colorize(t.error.slice(0, 40), 31);
  return '—';
}

function firstLine(s: string): string {
  return s.split('\n').find(l => l.trim().length > 0)?.trim() ?? '';
}

function formatTurn(t: TurnEntry, raw: boolean): string {
  const when = relativeTime(t.ts).padEnd(8);
  const dur = (t.duration_ms < 1000
    ? `${t.duration_ms}ms`
    : `${(t.duration_ms / 1000).toFixed(1)}s`).padStart(7);
  const role = t.role.padEnd(24);
  const sev = severityCell(t).padEnd(20);
  const preview = (() => {
    const req = firstLine(t.request_preview).slice(0, 60);
    return colorize(req, 90);
  })();
  const head = `${statusGlyph(t)} ${when} ${dur} ${role} ${sev} ${preview}`;
  if (!raw) return head;
  // raw mode: include full request + response previews on subsequent lines
  const blocks: string[] = [head];
  blocks.push(colorize('  request:', 90));
  blocks.push('    ' + t.request_preview.replace(/\n/g, '\n    '));
  if (t.response_preview) {
    blocks.push(colorize('  response:', 90));
    blocks.push('    ' + t.response_preview.replace(/\n/g, '\n    '));
  }
  if (t.error) {
    blocks.push(colorize('  error:', 31) + ' ' + t.error);
  }
  return blocks.join('\n');
}

function cmdLogs(rest: string[]): void | Promise<void> {
  let n = 20;
  let raw = false;
  let live = false;

  if (inDockerMode()) {
    // In docker mode the meaningful logs come from the container itself: MCP
    // boot, dispatcher routing, worker tmux output. Forward to `docker logs`
    // and let docker handle the --follow / --tail semantics.
    const followFlag = rest.includes('--live') || rest.includes('-f') ? ['--follow'] : [];
    const tailArg = (() => {
      for (const arg of rest) {
        const parsed = parseInt(arg, 10);
        if (parsed > 0) return String(parsed);
      }
      return '200';
    })();
    spawnSync('docker', ['logs', '--tail', tailArg, ...followFlag, 'synkro-server'], { stdio: 'inherit' });
    return;
  }

  for (const arg of rest) {
    if (arg === '--raw' || arg === '-r') raw = true;
    else if (arg === '--live' || arg === '-f') live = true;
    else if (arg === '--tmux') {
      // Escape hatch: show the original raw pueue/tmux output for the task.
      console.log(tailLogs(80));
      return;
    } else {
      const parsed = parseInt(arg, 10);
      if (parsed > 0) n = parsed;
    }
  }

  const header = '  ' + colorize('status when     dur     role                     severity             request', 90);

  // Print backlog (newest first) — same as non-live mode.
  const turns = readRecentTurns(n);
  if (turns.length === 0) {
    if (!live) {
      console.log(`No turns logged yet at ${TURN_LOG_PATH}.`);
      console.log('Run a few requests through the channel (synkro local-cc test) and try again.');
      return;
    }
    console.log(`No turns logged yet at ${TURN_LOG_PATH} — waiting for new entries… (Ctrl-C to exit)`);
  } else {
    console.log(`Last ${turns.length} channel turn(s) (newest first):`);
    console.log(header);
    for (const t of turns) console.log('  ' + formatTurn(t, raw));
  }

  if (!live) {
    if (!raw) console.log('  ' + colorize('(use --raw / -r to see full payloads, --live / -f to follow)', 90));
    return;
  }

  // Live tail. Follow the JSONL log; print each new entry as it arrives.
  // Returning a Promise keeps the process alive until SIGINT.
  return new Promise<void>(resolve => {
    console.log('  ' + colorize('— following new turns (Ctrl-C to exit) —', 90));
    const stop = followTurns(t => {
      console.log('  ' + formatTurn(t, raw));
    });
    const onSigint = () => {
      stop();
      process.removeListener('SIGINT', onSigint);
      resolve();
    };
    process.on('SIGINT', onSigint);
  });
}

function cmdAttach(rest: string[]): void {
  assertTmuxInstalled();
  const readonly = rest.some(a => a === '--readonly' || a === '-r');

  // Verify the session exists before tmux drops the user into a confusing state.
  const has = spawnSync('tmux', ['has-session', '-t', `=${TMUX_SESSION_NAME}`], { encoding: 'utf-8' });
  if (has.status !== 0) {
    console.error(`No tmux session '${TMUX_SESSION_NAME}' running. Start it with: synkro local-cc start`);
    process.exit(1);
  }

  if (!process.stdout.isTTY) {
    console.error('attach requires a TTY. Run this command directly in your terminal, not piped.');
    process.exit(1);
  }

  console.log(`Attaching to tmux session '${TMUX_SESSION_NAME}'${readonly ? ' (read-only)' : ''}.`);
  console.log('Detach with Ctrl-B then D. (Do not press Ctrl-C — that would interrupt claude.)');
  console.log();

  const args = readonly
    ? ['attach-session', '-r', '-t', TMUX_SESSION_NAME]
    : ['attach-session', '-t', TMUX_SESSION_NAME];
  const r = spawnSync('tmux', args, { stdio: 'inherit' });
  process.exit(r.status ?? 0);
}

async function cmdTest(): Promise<void> {
  // In docker mode the channel is published on the HOST at SYNKRO_HOST_GRADER_PORT
  // (default 18929 → container 8929). CHANNEL_PORT (8929) is the IN-container port and
  // isn't bound on the host, so the default would always "connection refused" even when
  // grading is healthy. Use the docker-mapped host port when in docker mode.
  const port = inDockerMode()
    ? parseInt(process.env.SYNKRO_HOST_GRADER_PORT || '18929', 10)
    : CHANNEL_PORT;
  console.log(`Sending smoke-test grading request through the channel (127.0.0.1:${port})...`);
  const result = await submitToChannel(
    'grade-bash',
    'Command: echo "hello world"\nIntent: testing channel connectivity',
    { port },
  );
  console.log('Raw reply:');
  console.log(result);
}

function cmdInstall(): void {
  const r = installLocalCC();
  console.log(`Reinstalled plugin at ${r.pluginPath}`);
}

/**
 * Re-export Claude Code credentials from the macOS keychain into the
 * host-side file the container bind-mounts. Called by the launchd refresh
 * agent every 6 hours, and exposed as `synkro local-cc refresh-creds` for
 * manual invocation if the user just re-authed.
 *
 * On Linux this is a no-op — creds already live in a file the container can
 * read directly via bind mount.
 */
function cmdRefreshCreds(): void {
  if (!needsKeychainBridge()) {
    console.log('No-op on this platform — credentials are already file-based.');
    return;
  }
  const refreshed = refreshCreds();
  if (refreshed) {
    console.log(`Exported claude credentials to ${CLAUDE_CREDS_FILE}`);
  } else {
    console.warn('No Claude Code credentials found in the keychain.');
    console.warn('Run `claude login` first, then re-run this command.');
    process.exitCode = 1;
  }
  if (credsAreStale()) {
    console.warn('⚠ Exported credentials are older than the refresh interval.');
  }
}

export async function localCcCommand(args: string[]): Promise<void> {
  const sub = args[0] ?? '';
  try {
    switch (sub) {
      case 'enable':   await cmdEnable(); break;
      case 'disable':  cmdDisable(); break;
      case 'status':   await cmdStatus(); break;
      case 'start':    await cmdStart(args.slice(1)); break;
      case 'stop':     cmdStop(); break;
      case 'restart':  await cmdRestart(args.slice(1)); break;
      case 'install':  cmdInstall(); break;
      case 'logs':     await cmdLogs(args.slice(1)); break;
      case 'attach':   cmdAttach(args.slice(1)); break;
      case 'test':     await cmdTest(); break;
      case 'refresh-creds': cmdRefreshCreds(); break;
      case '':
      case 'help':
      case '--help':
      case '-h':
        printHelp(); break;
      default:
        console.error(`Unknown subcommand: ${sub}`);
        printHelp();
        process.exit(1);
    }
  } catch (err) {
    console.error((err as Error).message);
    process.exit(1);
  }
}
